import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	containerClassName?: React.HTMLProps<HTMLDivElement>['className']
	autoGrow?: boolean
	maxHeight?: number
	containerStyle?: React.CSSProperties
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
	({ className, containerStyle, autoGrow, containerClassName, maxHeight, children, ...props }, ref) => {
		let thisRef = React.useRef<HTMLTextAreaElement>(null)

		const performAutoGrow = React.useCallback((element: HTMLTextAreaElement) => {
			let effectiveMaxHeight = maxHeight ?? Infinity
			element.style.height = 'auto'
			element.style.height = Math.min(element.scrollHeight, effectiveMaxHeight) + 'px'
			if (element.scrollHeight > effectiveMaxHeight) {
				element.style.overflowY = 'auto'
			} else {
				element.style.overflowY = 'hidden'
			}
		}, [maxHeight])

		// run auto-gro on mount
		React.useEffect(() => {
			if (autoGrow && thisRef.current) {
				performAutoGrow(thisRef.current)
			}
		}, [autoGrow, performAutoGrow])

		React.useEffect(() => {
			const handleAutoGrow = () => {
				const element = thisRef.current
				if (!element) return
				performAutoGrow(element)
			}

			if (autoGrow) {
				const element = thisRef.current
				const form = element?.form
				let scheduledFrame: number | null = null
				let scheduledTimeout: ReturnType<typeof setTimeout> | null = null
				const scheduleAutoGrow = () => {
					if (scheduledFrame !== null || scheduledTimeout !== null) return
					if (typeof requestAnimationFrame === 'function') {
						scheduledFrame = requestAnimationFrame(() => {
							scheduledFrame = null
							handleAutoGrow()
						})
						return
					}
					scheduledTimeout = setTimeout(() => {
						scheduledTimeout = null
						handleAutoGrow()
					}, 0)
				}
				const handleInput = () => {
					handleAutoGrow()
					scheduleAutoGrow()
				}
				if (element) {
					element.addEventListener('input', handleInput)
					form?.addEventListener('reset', scheduleAutoGrow)
				}

				return () => {
					const element = thisRef.current
					if (element) {
						element.removeEventListener('input', handleInput)
					}
					if (scheduledFrame !== null) {
						cancelAnimationFrame(scheduledFrame)
					}
					if (scheduledTimeout !== null) {
						clearTimeout(scheduledTimeout)
					}
					form?.removeEventListener('reset', scheduleAutoGrow)
				}
			}
		}, [autoGrow, performAutoGrow])

		return (
			<div
				style={containerStyle}
				className={cn(
					'custom-scrollbar dark:bg-input/30 flex min-h-[80px] w-full flex-col rounded-xl border border-input bg-input px-3 py-2 outline-none transition-[color,box-shadow,border-color] duration-200 ease-out selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
					'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
					containerClassName,
				)}
			>
				<textarea
					// Safari can paint a horizontal scrollbar when auto-grow only sets overflowY.
					// Remove direct focus ring from textarea, keep outline none
					className={cn(
						'aria-[invalid]:border-input-invalid overflow-x-hidden border-none bg-transparent text-xs font-normal text-foreground/70 placeholder-foreground/20 outline-none disabled:cursor-not-allowed disabled:opacity-50',
						className,
					)}
					ref={el => {
						// @ts-ignore
						thisRef.current = el
						// @ts-ignore
						if (ref) ref.current = el
					}}
					{...props}
				/>
				{children}
			</div>
		)
	},
)
Textarea.displayName = 'Textarea'

export { Textarea }
